import { useTranslation } from 'react-i18next';
import { UserAlliance } from '../../types/alliance';
import ProfileAvatar from '../common/ProfileAvatar';
import { DropdownTypeahead } from '../common/DropdownTypeahead';
import { useAllianceStore } from '../../stores/allianceStore';
import { InputGroup } from '../common/InputGroup';
import { useState } from 'react';
import { Button } from '../common/Button';
import { Link } from 'react-router-dom';
import AllianceConfirmDeleteModal from './AllianceConfirmDeleteModal';
import { resolveServiceCategoryLabel } from '../../utils/serviceCategoryLabel';

interface Props {
  activeAlliances: UserAlliance[];
  loading: boolean;
}

const ActiveAlliance = ({ alliance }: { alliance: UserAlliance }) => {
  const { t, i18n } = useTranslation(['alliances', 'common']);
  const [error, setError] = useState<string | null>(null);
  const [settingHighlightedServiceCategory, setSettingHighlightedServiceCategory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { setHighlightedServiceCategory } = useAllianceStore();
  const serviceOptions = alliance.user.service_categories.map(category => ({
    id: category.id,
    label: resolveServiceCategoryLabel(category, i18n.language, t),
  }));

  const handleServiceCategoryChange = async (
    _value: string,
    item?: { id: number; label: string }
  ) => {
    if (item) {
      try {
        setSettingHighlightedServiceCategory(true);
        setError(null);
        const result = await setHighlightedServiceCategory(
          alliance.alliance_id,
          alliance.is_user1_current,
          item.id
        );
        if (result.error) {
          setError(t('error.generic', { ns: 'common' }));
        }
      } catch (error) {
        console.error(error);
        setError(t('error.generic', { ns: 'common' }));
      } finally {
        setSettingHighlightedServiceCategory(false);
      }
    }
  };

  return (
    <div className="mb-6 flex flex-col gap-4 md:mb-8">
      <Link to={`/freelancers/${alliance.user.id}`} className="flex items-center gap-4">
        <ProfileAvatar
          profile_id={alliance.user.id}
          first_name={alliance.user.first_name}
          avatar_url={alliance.user.avatar_url}
          className="bg-primary text-white"
        />

        <span className="font-bold text-secondary">
          {alliance.user.first_name} {alliance.user.last_name}
        </span>
      </Link>
      <InputGroup
        label={t('highlightedRole')}
        error={!!error}
        errorMessage={error}
        disabled={settingHighlightedServiceCategory}
      >
        <DropdownTypeahead<{ id: number; label: string }>
          value={alliance.user_highlighted_service_category?.label || ''}
          onChange={handleServiceCategoryChange}
          options={serviceOptions}
          getOptionLabel={item => item.label}
          getOptionValue={item => item.id}
          placeholder={t('chooseRole')}
          className="mt-1"
        />
      </InputGroup>
      <Button
        className="w-full md:w-auto md:self-end md:px-8"
        onClick={() => setShowDeleteModal(true)}
      >
        {t('remove')}
      </Button>

      {showDeleteModal && (
        <AllianceConfirmDeleteModal alliance={alliance} onClose={() => setShowDeleteModal(false)} />
      )}
    </div>
  );
};

const ActiveAlliances = ({ activeAlliances }: Props) => {
  const { t } = useTranslation('alliances');

  return (
    <div className="flex-1 rounded-2xl border-2 border-gray-300 bg-white p-4 shadow-xl md:p-6">
      <h2 className="mb-2 text-xl font-semibold text-secondary md:mb-4">{t('activeAlliances')}</h2>

      {activeAlliances.length === 0 && (
        <div className="mb-4 text-sm text-gray-500">{t('noActiveAlliances')}</div>
      )}

      {activeAlliances.map((alliance, index) => (
        <div key={alliance.alliance_id}>
          <ActiveAlliance alliance={alliance} />
          {index < activeAlliances.length - 1 && <hr className="my-4 md:my-6" />}
        </div>
      ))}
    </div>
  );
};

export default ActiveAlliances;
